import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./AddBlog.css";
import "./Treat/Treat.css";
import { getContentPreferences, FALLBACK_OPTIONS } from "../services/preferencesApi";

const FILTERS = [
  { name: "Normal", css: "none" },
  { name: "Clarendon", css: "contrast(1.2) saturate(1.35) brightness(1.05)" },
  { name: "Gingham", css: "brightness(1.06) hue-rotate(-10deg) contrast(0.9)" },
  { name: "Moon", css: "grayscale(1) contrast(1.1) brightness(1.1)" },
  { name: "Lark", css: "brightness(1.12) contrast(0.85) saturate(1.2)" },
  { name: "Reyes", css: "sepia(0.22) brightness(1.1) contrast(0.85) saturate(0.75)" },
  { name: "Juno", css: "saturate(1.4) contrast(1.1) brightness(1.05) hue-rotate(-5deg)" },
  { name: "Slumber", css: "saturate(0.7) brightness(1.05) sepia(0.15)" },
  { name: "Crema", css: "saturate(0.85) contrast(0.95) brightness(1.12) sepia(0.1)" },
  { name: "Ludwig", css: "saturate(0.9) contrast(1.05) brightness(1.08) sepia(0.06)" },
  { name: "Aden", css: "hue-rotate(-20deg) saturate(0.85) brightness(1.2) contrast(0.85)" },
  { name: "Perpetua", css: "saturate(1.1) contrast(1.1) brightness(1.02)" },
  { name: "Neon", css: "saturate(2.4) hue-rotate(30deg) contrast(1.25)" },
  { name: "Drama", css: "contrast(1.5) brightness(0.86) saturate(1.35)" },
  { name: "Fade", css: "brightness(1.14) contrast(0.76) saturate(0.7)" },
  { name: "Vintage", css: "sepia(0.55) contrast(1.15) brightness(0.88)" },
];

function EditBlog() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [toast, setToast] = useState(null);

  const [hideComments, setHideComments] = useState(false);
  const [allowShare, setAllowShare] = useState(true);
  const [allowSave, setAllowSave] = useState(true);
  // BUY ME A TREAT — the server applies the payout-setup rule when this is
  // switched on; switching it off is always allowed.
  const [treatsEnabled, setTreatsEnabled] = useState(false);

  // One language + one category. A blog published before these existed has
  // neither; it may be saved as it is, or given them here.
  const [language, setLanguage] = useState("");
  const [category, setCategory] = useState("");
  const [contentOptions, setContentOptions] = useState(FALLBACK_OPTIONS);

  // existing media (from server) — kept unless replaced
  const [existingImage, setExistingImage] = useState(null);
  const [existingVideo, setExistingVideo] = useState(null);
  const [imageAspectRatio, setImageAspectRatio] = useState("4:5");

  // new media (if user picks a replacement)
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newVideo, setNewVideo] = useState(null);
  const [newVideoPreview, setNewVideoPreview] = useState(null);

  const [selectedFilter, setSelectedFilter] = useState(0);

  // Guard against setState-after-unmount once we've kicked off navigation
  const navigatedRef = useRef(false);
  const mountedRef = useRef(true);

  const imageRef = useRef();
  const videoRef = useRef();

  const token = localStorage.getItem("token");
  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  const showToast = (msg, type = "info") => {
    if (!mountedRef.current) return;
    setToast({ msg, type });
    setTimeout(() => {
      if (mountedRef.current) setToast(null);
    }, 3000);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── LOAD EXISTING BLOG ──
  useEffect(() => {
    const load = async () => {
      try {
        setFetching(true);
        const res = await axios.get(`${BASE_URL}/api/blogs/${id}`);
        const blog = res.data;
        if (!blog) {
          showToast("Blog not found ❌", "error");
          navigate("/pro", { replace: true });
          return;
        }
        // client-side ownership guard (backend also enforces this)
        const me = localStorage.getItem("username");
        if (blog.author !== me) {
          showToast("You can't edit this blog ❌", "error");
          navigate("/pro", { replace: true });
          return;
        }
        setTitle(blog.title || "");
        setContent(blog.content || "");
        setAuthor(blog.author || "");
        setExistingImage(blog.image || null);
        setExistingVideo(blog.video || null);
        setImageAspectRatio(blog.aspectRatio || "4:5");
        setHideComments(Boolean(blog.hideComments));
        setAllowShare(blog.allowShare !== false);
        setAllowSave(blog.allowSave !== false);
        setTreatsEnabled(blog.treatsEnabled === true);
        setLanguage(blog.language || "");
        setCategory(blog.category || "");
      } catch (err) {
        console.error(err);
        showToast("Failed to load blog ❌", "error");
        navigate("/pro", { replace: true });
      } finally {
        if (mountedRef.current) setFetching(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    getContentPreferences()
      .then((prefs) => { if (mountedRef.current) setContentOptions(prefs.options); })
      .catch(() => {});
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewImage(file);
    setNewImagePreview(URL.createObjectURL(file));
    setSelectedFilter(0);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const videoEl = document.createElement("video");
    videoEl.src = URL.createObjectURL(file);
    videoEl.onloadedmetadata = () => {
      if (videoEl.duration > 60) {
        showToast("Video must be under 1 minute ❌", "error");
        videoRef.current.value = "";
        setNewVideo(null);
        setNewVideoPreview(null);
      } else {
        setNewVideo(file);
        setNewVideoPreview(URL.createObjectURL(file));
      }
    };
  };

  const buildFilter = (fi) => (FILTERS[fi].css === "none" ? "" : FILTERS[fi].css);

  const bakeImage = async (srcUrl, filterCss) => {
    const img = new Image();
    img.src = srcUrl;
    await new Promise((r) => (img.onload = r));
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.filter = filterCss;
    ctx.drawImage(img, 0, 0);
    return new Promise((res) => canvas.toBlob((blob) => res(blob), "image/jpeg", 0.92));
  };

  const saveBlog = async (e) => {
    e.preventDefault();
    if (title && title.length > 100) return showToast("Title max 100 chars", "error");
    if (content && content.length > 500) return showToast("Content max 500 chars", "error");

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("title", title);
      formData.append("content", content);
      formData.append("aspectRatio", imageAspectRatio);
      formData.append("hideComments", hideComments);
      formData.append("allowShare", allowShare);
      formData.append("allowSave", allowSave);
      formData.append("treatsEnabled", treatsEnabled);
      if (language) formData.append("language", language);
      if (category) formData.append("category", category);

      if (newImage && newImagePreview) {
        const filterCss = buildFilter(selectedFilter);
        if (filterCss) {
          const baked = await bakeImage(newImagePreview, filterCss);
          formData.append("image", new File([baked], newImage.name, { type: "image/jpeg" }));
        } else {
          formData.append("image", newImage);
        }
      }
      if (newVideo) formData.append("video", newVideo);

      await axios.put(`${BASE_URL}/api/blogs/edit/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });

      // Navigate immediately — don't setState after this component unmounts.
      navigatedRef.current = true;
      navigate("/pro", { replace: true, state: { toast: "Blog updated ✅" } });
    } catch (err) {
      console.error(err);
      if (mountedRef.current) {
        showToast(err.response?.data?.message || "Update failed ❌", "error");
      }
    } finally {
      if (mountedRef.current && !navigatedRef.current) setLoading(false);
    }
  };

  const titlePct = (title.length / 100) * 100;
  const contentPct = (content.length / 500) * 100;

  if (fetching) {
    return (
      <div className="addblog-wrapper">
        <Navbar />
        <div className="addblog-container">
          <div className="addblog-box" style={{ textAlign: "center", padding: "60px 20px" }}>
            <span className="btn-spinner" />
            <p style={{ marginTop: 16, color: "rgba(226,232,240,0.5)" }}>Loading blog…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="addblog-wrapper">
      <Navbar />

      {toast && <div className={`addblog-toast addblog-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="addblog-container">
        <div className="addblog-box">
          <div className="addblog-header">
            <div className="addblog-badge">✎ Edit</div>
            <h2 className="addblog-title">Edit Blog</h2>
            <p className="addblog-sub">Update your post</p>
          </div>

          <form onSubmit={saveBlog} className="addblog-form">
            <div className="field-group">
              <label className="field-label">📝 Title</label>
              <input
                type="text"
                value={title}
                maxLength={100}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading}
                className="addblog-input"
              />
              <div className="char-bar-row">
                <div className="char-bar">
                  <div
                    className="char-bar-fill"
                    style={{
                      width: `${titlePct}%`,
                      background: titlePct > 90 ? "#ef4444" : titlePct > 70 ? "#f59e0b" : "#a78bfa",
                    }}
                  />
                </div>
                <span className="char-count">{title.length}/100</span>
              </div>
            </div>

            <div className="field-group">
              <label className="field-label">💬 Content</label>
              <textarea
                value={content}
                maxLength={500}
                rows={6}
                onChange={(e) => setContent(e.target.value)}
                disabled={loading}
                className="addblog-input addblog-textarea"
              />
              <div className="char-bar-row">
                <div className="char-bar">
                  <div
                    className="char-bar-fill"
                    style={{
                      width: `${contentPct}%`,
                      background: contentPct > 90 ? "#ef4444" : contentPct > 70 ? "#f59e0b" : "#06b6d4",
                    }}
                  />
                </div>
                <span className="char-count">{content.length}/500</span>
              </div>
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="editBlogLanguage">🌐 Language</label>
              <select
                id="editBlogLanguage"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={loading}
                className="addblog-input addblog-select"
              >
                {/* Only a legacy blog can be without one; once set it can be changed, not removed. */}
                {!language && <option value="">Select a language…</option>}
                {contentOptions.languages.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="editBlogCategory">🏷 Category</label>
              <select
                id="editBlogCategory"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={loading}
                className="addblog-input addblog-select"
              >
                {!category && <option value="">Select a category…</option>}
                {contentOptions.categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="field-group">
              <label className="field-label">👤 Author</label>
              <input type="text" value={author} readOnly className="addblog-input addblog-readonly" />
            </div>

            {/* ── POST SETTINGS ── */}
            <div className="field-group">
              <label className="field-label">⚙ Post Settings</label>
              <div className="post-settings-row">
                <label className="post-toggle">
                  <input
                    type="checkbox"
                    checked={hideComments}
                    onChange={(e) => setHideComments(e.target.checked)}
                    disabled={loading}
                  />
                  <span>Hide comments</span>
                </label>
                <label className="post-toggle">
                  <input
                    type="checkbox"
                    checked={!allowShare}
                    onChange={(e) => setAllowShare(!e.target.checked)}
                    disabled={loading}
                  />
                  <span>Turn off sharing</span>
                </label>
                <label className="post-toggle">
                  <input
                    type="checkbox"
                    checked={!allowSave}
                    onChange={(e) => setAllowSave(!e.target.checked)}
                    disabled={loading}
                  />
                  <span>Turn off saving</span>
                </label>
              </div>
            </div>

            {/* ── BUY ME A TREAT ── */}
            <div className="field-group treat-enable">
              <label className="post-toggle">
                <input
                  type="checkbox"
                  checked={treatsEnabled}
                  onChange={(e) => setTreatsEnabled(e.target.checked)}
                  disabled={loading}
                />
                <span>🎁 Enable Buy Me a Treat</span>
              </label>
              <p className="treat-enable-desc">Readers can send you treats to appreciate your writing.</p>
              <p className="treat-enable-desc">
                Need to add bank details? <a href="/treat-payouts">Complete Payout Setup →</a>
              </p>
            </div>

            {/* ── IMAGE ── */}
            <div className="field-group">
              <label className="field-label">🖼 Image</label>

              {!newImagePreview && existingImage && (
                <div style={{ marginBottom: 10 }}>
                  <img
                    src={existingImage}
                    alt="current"
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      maxHeight: 300,
                      objectFit: "contain",
                      background: "rgba(0,0,0,0.2)",
                    }}
                  />
                  <p style={{ fontSize: 12, color: "rgba(226,232,240,0.4)", marginTop: 6 }}>
                    Current image — choose a new file below to replace it
                  </p>
                </div>
              )}

              <div className="file-input-wrap">
                <input
                  ref={imageRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={loading}
                  className="addblog-file"
                  id="editImgInput"
                />
                <label htmlFor="editImgInput" className="file-label">
                  {newImage ? `✅ ${newImage.name}` : existingImage ? "Replace image..." : "Choose image..."}
                </label>
              </div>

              {newImagePreview && (
                <div className="media-editor">
                  <div className="media-editor-preview-wrap">
                    <img
                      src={newImagePreview}
                      className="media-editor-preview"
                      style={{ filter: buildFilter(selectedFilter) }}
                      alt="new preview"
                    />
                  </div>
                  <div className="med-filter-strip">
                    {FILTERS.map((f, i) => (
                      <button
                        type="button"
                        key={f.name}
                        className={`med-filter-btn${selectedFilter === i ? " active" : ""}`}
                        onClick={() => setSelectedFilter(i)}
                      >
                        <div
                          className="med-filter-thumb"
                          style={{ backgroundImage: `url(${newImagePreview})`, filter: f.css }}
                        />
                        <span className="med-filter-name">{f.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── VIDEO ── */}
            <div className="field-group">
              <label className="field-label">
                🎥 Video <span className="optional">(max 1 min)</span>
              </label>

              {!newVideoPreview && existingVideo && (
                <div style={{ marginBottom: 10 }}>
                  <video src={existingVideo} controls style={{ width: "100%", borderRadius: 12, maxHeight: 300 }} />
                  <p style={{ fontSize: 12, color: "rgba(226,232,240,0.4)", marginTop: 6 }}>
                    Current video — choose a new file below to replace it
                  </p>
                </div>
              )}

              <div className="file-input-wrap">
                <input
                  ref={videoRef}
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  disabled={loading}
                  className="addblog-file"
                  id="editVidInput"
                />
                <label htmlFor="editVidInput" className="file-label">
                  {newVideo ? `✅ ${newVideo.name}` : existingVideo ? "Replace video..." : "Choose video..."}
                </label>
              </div>

              {newVideoPreview && (
                <video
                  src={newVideoPreview}
                  controls
                  className="media-editor-preview media-editor-video"
                  style={{ marginTop: 10 }}
                />
              )}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                className="addblog-btn"
                style={{ background: "rgba(255,255,255,0.06)" }}
                onClick={() => navigate("/pro")}
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="addblog-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="btn-spinner" /> Saving…
                  </>
                ) : (
                  <>💾 Save Changes</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditBlog;